import React from "react"

import dayjs from "dayjs"
import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import { OperationContext, gql, useMutation } from "urql"
import * as Yup from "yup"

import { AcademicCapIcon } from "@heroicons/react/24/outline"

import { Color } from "~tailwindui/types/enums"

import { AlertMessageBox, Button } from "~tailwindui/Basics"
import {
  AutocompleteSelect,
  CheckboxInput,
  FileInput,
  SelectInput,
  TextInput,
  handleFailure,
} from "~tailwindui/Form"
import Modal from "~tailwindui/Modal"

import { degreeLevelOptions, degreeTypeOptions } from "src/enums"
import readFile from "src/readFile"
import { RunMutation } from "src/types"

const ValidationSchema = Yup.object().shape({
  college: Yup.string().required("College/University name is required"),
  degree: Yup.string().required("Field of study is required"),
  level: Yup.string().required("Degree level is required"),
  levelOther: Yup.mixed().when("level", {
    is: "other",
    then: Yup.string().required("Please describe your degree level"),
  }),
  year: Yup.number()
    .required("Year of graduation is required")
    .max(dayjs().year() + 4, "Degree date must be within the next 4 years"),
  country: Yup.mixed().when("inUsa", {
    is: false,
    then: Yup.string().required("Country for college/university is required"),
  }),
  degreeType: Yup.string().required("Degree Type is required"),
})

export type DegreeFormModalProps = {
  closeForm: () => void
  refetchQuery: (opts: Partial<OperationContext>) => void
}

const DegreeFormModal: React.FC<DegreeFormModalProps> = ({
  closeForm,
  refetchQuery,
}) => {
  const formState = {
    college: "",
    degree: "",
    level: "",
    levelOther: "",
    year: "",
    country: "",
    degreeType: "",
    inUsa: false,
    transcript: undefined,
  }

  const [, runMutation]: [any, RunMutation] = useMutation(addDegreeMutation)

  const handleSubmit = (values, actions) => {
    readFile(values.transcript)
      .then(file => {
        values.transcript = file
        runMutation({ input: values })
          .then(
            response => {
              const { failures } = response.data.addDegree
              if (failures.length > 0) {
                handleFailure(actions, failures)
              } else {
                refetchQuery({ requestPolicy: "network-only" })
                closeForm()
                NotificationManager.success(
                  "An admin will be notified to approve your degree"
                )
              }
            },
            () => handleFailure(actions)
          )
          .catch(() => handleFailure(actions))
      })
      .catch(() => handleFailure(actions))
  }

  return (
    <Modal.Dialog hideButton isOpen={true} closeModal={closeForm}>
      <Modal.Header Icon={AcademicCapIcon}>Add Degree</Modal.Header>
      <Formik
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        initialValues={formState}
        validateOnChange={false}
      >
        {({ values, submitForm, isSubmitting, status }) => (
          <Form className="flex max-h-[calc(70vh-90px)] flex-col">
            <Modal.Body>
              <AutocompleteSelect
                creatable
                name="college"
                label="College/University"
                resource="colleges"
                className="w-20"
                valueColumn="name"
                portal
                clearable
              />

              <TextInput
                name="degree"
                label="Field of Study (e.g. Elementary Education)"
              />
              <SelectInput
                name="degreeType"
                label="Degree Type"
                options={degreeTypeOptions}
                portal
              />
              <SelectInput
                name="level"
                label="Level"
                options={degreeLevelOptions}
                portal
              />
              {values.level === "other" && (
                <TextInput
                  name="levelOther"
                  label="What is your degree level?"
                />
              )}

              <TextInput name="year" label="Year of Graduation" type="number" />
              <CheckboxInput
                name="inUsa"
                label="This college is located in the US"
                checkboxType="single"
              />

              {!values.inUsa && (
                <AutocompleteSelect
                  name="country"
                  label="Country where degree was obtained"
                  resource="countries"
                  creatable
                  className="w-20"
                  valueColumn="name"
                  portal
                />
              )}

              <FileInput name="transcript" label="Transcript (optional)" />
            </Modal.Body>
            <Modal.Footer>
              <div className="flex flex-col">
                {Array.isArray(status) && (
                  <AlertMessageBox>{status}</AlertMessageBox>
                )}
                <div className="flex justify-end space-x-2">
                  <Button soft color={Color.Red} onClick={() => closeForm()}>
                    Cancel
                  </Button>
                  <Button soft onClick={submitForm} loading={isSubmitting}>
                    Submit
                  </Button>
                </div>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal.Dialog>
  )
}

const addDegreeMutation = gql`
  mutation AddTutorDegree($input: DegreeInputObject!) {
    addDegree(input: $input) {
      failures {
        message
      }
    }
  }
`

export default DegreeFormModal
